import assertNever from 'assert-never';

import { FirmwareUpdateJobRequest, ScheduledJobType } from '@shure/cloud/shared/models/devices';
import { DeviceModel, DeviceStatus } from '@shure/shared/angular/data-access/system-api/models';

import { ScheduledJobRequestFragment } from '../graphql/generated/cloud-sys-api';

import {
	mapSysApiFirmwareUpdateExecutionStage,
	mapSysApiFirmwareUpdateRequestError,
	mapSysApiFirmwareUpdateRequestStatus
} from './map-firmware-update-requests';
import { mapDeviceModelFromSysApi } from './utils';

export function mapScheduledJobRequestFromSysApi(
	scheduledJobRequest: ScheduledJobRequestFragment | null | undefined
): FirmwareUpdateJobRequest | undefined {
	if (scheduledJobRequest === undefined || scheduledJobRequest === null) {
		return undefined;
	}

	if (scheduledJobRequest.details.__typename === 'FirmwareUpdateJobRequestDetails') {
		const device = scheduledJobRequest.details.task.device;
		const isKnown = device.__typename === 'Device';
		const result = scheduledJobRequest.details.result;

		const jobRequest: FirmwareUpdateJobRequest = {
			id: scheduledJobRequest.id,
			type: ScheduledJobType.FirmwareUpdate,
			status: scheduledJobRequest.status,
			jobId: scheduledJobRequest.job.jobId,
			executeAt: scheduledJobRequest.job.executeAt,
			// Only known devices (Device type) have node IDs; unknown devices don't
			task: {
				packageKey: scheduledJobRequest.details.task.packageKey,
				packageVersion: scheduledJobRequest.details.task.packageVersion,
				hosted: scheduledJobRequest.details.task.hosted,
				device: {
					known: isKnown,
					nodeId: isKnown ? device.id : undefined,
					hardwareId: device.hardwareId,
					model: isKnown
						? device.interface?.model
							? mapDeviceModelFromSysApi(device.interface?.model)
							: DeviceModel.UNKNOWN
						: mapDeviceModelFromSysApi(device.model),
					status: isKnown ? <DeviceStatus>device.status : DeviceStatus.Offline,
					name: isKnown ? device.features.name?.name : (device.name ?? undefined),
					roomName: isKnown ? (device.features.room?.room?.name ?? '') : (device.roomName ?? ''),
					currentFirmwareVersion: isKnown
						? device.features.firmware?.version
						: (device.firmwareVersion ?? undefined)
				}
			},
			result: result
				? {
						status: mapSysApiFirmwareUpdateRequestStatus(result.status),
						executingStage: mapSysApiFirmwareUpdateExecutionStage(result.stage),
						error: mapSysApiFirmwareUpdateRequestError(result.error),
						progress: result.percentComplete,
						// for convenience, package and version are copied into the result section.
						package: scheduledJobRequest.details.task.packageKey,
						version: scheduledJobRequest.details.task.packageVersion
					}
				: undefined
		};

		return jobRequest;
	}

	return assertNever(scheduledJobRequest.details.__typename);
}
